import { z } from "zod";

/**
 * Catalogue of API error codes. Clients branch on `code`, never by parsing
 * `message`, which is human-facing text.
 */
export const API_ERROR_CODES = [
  "BAD_REQUEST",
  "VALIDATION_ERROR",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  /**
   * The credentials are valid but the organization is not being served.
   * Distinct from FORBIDDEN so the frontend can say so plainly instead of
   * showing a generic access-denied page to a customer whose account is fine.
   */
  "ORGANIZATION_SUSPENDED",
  "NOT_FOUND",
  "CONFLICT",
  "PAYLOAD_TOO_LARGE",
  "RATE_LIMITED",
  "UNPROCESSABLE",
  "INTERNAL_ERROR",
  "SERVICE_UNAVAILABLE",
] as const;

export const apiErrorCodeSchema = z.enum(API_ERROR_CODES);
export type ApiErrorCode = z.infer<typeof apiErrorCodeSchema>;

/**
 * The single error shape across the API. `details` carries only information
 * that is safe to display: never stack traces, SQL, tokens or raw payloads.
 */
export const apiErrorSchema = z.object({
  error: z.object({
    code: apiErrorCodeSchema,
    message: z.string(),
    details: z.array(
      z.object({
        field: z.string().optional(),
        message: z.string(),
      }),
    ),
    requestId: z.string(),
  }),
});

export type ApiError = z.infer<typeof apiErrorSchema>;
